/**
 * TournamentReplayScreen Component
 *
 * Displays tournament match battle replays sequentially using BattleScreen.
 * Converts tournament battle format to standard battleState format.
 * Supports both single battle and 3v3 format.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import BattleScreen from './BattleScreen';

const TournamentReplayScreen = () => {
  const { setGameState, selectedReplay, setSelectedReplay, battleState, setBattleState } = useGame();

  const [currentBattleIndex, setCurrentBattleIndex] = useState(0);
  const [showingBattle, setShowingBattle] = useState(false);
  const [showMatchResult, setShowMatchResult] = useState(false);
  const [showScoreScreen, setShowScoreScreen] = useState(false);
  const [battleResultsTracker, setBattleResultsTracker] = useState([null, null, null]); // Track results of battles

  // Helper to safely parse battle_results (can be string or object from DB)
  const parseBattleResults = (battle_results) => {
    if (!battle_results) return null;
    if (typeof battle_results === 'object') return battle_results;
    try {
      return JSON.parse(battle_results);
    } catch (e) {
      console.error('Failed to parse battle_results:', e);
      return null;
    }
  };

  const battleResults = parseBattleResults(selectedReplay?.battle_results);

  // Handle both old single-battle format and new 3v3 format
  const is3v3Format = battleResults?.battles && Array.isArray(battleResults.battles);
  const battles = is3v3Format ? battleResults.battles : (battleResults ? [battleResults] : []);
  const totalBattles = battles.length;

  // Watch for battle completion - don't auto-advance
  useEffect(() => {
    if (showingBattle && battleState?.serverResult) {
      const log = battleState.log || [];
      const logIndex = battleState.logIndex ?? 0;

      // Check if battle is complete
      if (logIndex >= log.length - 1 &&
          (battleState.player?.currentHP <= 0 || battleState.opponent?.currentHP <= 0)) {
        // Don't auto-advance - wait for handleBattleComplete to be called by BattleScreen Continue button
      }
    }
  }, [showingBattle, battleState?.logIndex, battleState?.player?.currentHP, battleState?.opponent?.currentHP]);

  const handleBattleComplete = (winner) => {
    // Record the result of this battle (for 3v3 battles, track wins)
    const newResults = [...battleResultsTracker];
    // In tournaments, pokemon1 is always "player" side
    newResults[currentBattleIndex] = winner === 'player' ? 'win' : 'loss';
    setBattleResultsTracker(newResults);

    setShowingBattle(false);
    setBattleState(null);

    // Show score screen after battle completes (only for 3v3)
    if (is3v3Format) {
      setShowScoreScreen(true);
    } else {
      // For single battle, go straight to result
      setShowMatchResult(true);
    }
  };

  const handleContinueToNextBattle = () => {
    setShowScoreScreen(false);

    // Move to next battle or show final result
    if (currentBattleIndex < totalBattles - 1) {
      setCurrentBattleIndex(currentBattleIndex + 1);
    } else {
      setShowMatchResult(true);
    }
  };

  const startBattle = (battleIndex) => {
    const battle = battles[battleIndex];
    if (!battle) return;

    // Convert tournament battle format to standard battleState format
    setBattleState({
      serverResult: {
        battleLog: battle.battleLog,
        winner: battle.winner,
        player1: {
          name: battle.pokemon1.name,
          type: battle.pokemon1.type,
          primaryType: Array.isArray(battle.pokemon1.type) ? battle.pokemon1.type[0] : battle.pokemon1.type,
          stats: battle.pokemon1.stats,
          movepool: battle.pokemon1.movepool || [],
          statusEffects: []
        },
        player2: {
          name: battle.pokemon2.name,
          type: battle.pokemon2.type,
          primaryType: Array.isArray(battle.pokemon2.type) ? battle.pokemon2.type[0] : battle.pokemon2.type,
          stats: battle.pokemon2.stats,
          movepool: battle.pokemon2.movepool || [],
          statusEffects: []
        }
      },
      log: battle.battleLog,
      logIndex: 0,
      tick: 0,
      player: {
        name: battle.pokemon1.name,
        primaryType: Array.isArray(battle.pokemon1.type) ? battle.pokemon1.type[0] : battle.pokemon1.type,
        stats: battle.pokemon1.stats,
        currentHP: battle.pokemon1.stats.HP,
        currentStamina: 100,
        statusEffects: [],
        movepool: battle.pokemon1.movepool || []
      },
      opponent: {
        name: battle.pokemon2.name,
        primaryType: Array.isArray(battle.pokemon2.type) ? battle.pokemon2.type[0] : battle.pokemon2.type,
        stats: battle.pokemon2.stats,
        currentHP: battle.pokemon2.stats.HP,
        currentStamina: 100,
        statusEffects: [],
        movepool: battle.pokemon2.movepool || []
      },
      displayLog: [],
      isTournamentReplay: true
    });

    setShowingBattle(true);
  };

  // Start battle when ready (called by score screen or initial load)
  useEffect(() => {
    if (battleResults && !showingBattle && !showMatchResult && !showScoreScreen && battles.length > 0) {
      // Start the current battle
      startBattle(currentBattleIndex);
    }
  }, [battleResults, currentBattleIndex, showScoreScreen]);

  if (!selectedReplay || !battleResults) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <div className="text-pocket-text">No replay data available</div>
      </div>
    );
  }

  // Show battle screen
  if (showingBattle && battleState) {
    return <BattleScreen onBattleComplete={handleBattleComplete} />;
  }

  // Show score screen between battles (for 3v3 format only)
  if (showScoreScreen && is3v3Format) {
    const wins = battleResultsTracker.filter(r => r === 'win').length;
    const losses = battleResultsTracker.filter(r => r === 'loss').length;
    const battlesCompleted = currentBattleIndex + 1;
    const battlesRemaining = totalBattles - battlesCompleted;
    const lastBattleResult = battleResultsTracker[currentBattleIndex];

    return (
      <div className="min-h-screen bg-pocket-bg p-4 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-card-lg p-8"
          >
            {/* Battle Result */}
            <div className="text-center mb-6">
              <div className={`text-5xl mb-3 ${lastBattleResult === 'win' ? 'text-green-500' : 'text-red-500'}`}>
                {lastBattleResult === 'win' ? <Trophy className="mx-auto" /> : '❌'}
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${lastBattleResult === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                Battle {battlesCompleted}: {lastBattleResult === 'win' ? 'Victory!' : 'Defeat'}
              </h2>
            </div>

            {/* Current Score */}
            <div className="bg-pocket-bg rounded-xl p-6 mb-6">
              <h3 className="text-center text-pocket-text-light font-semibold mb-4">Current Score</h3>
              <div className="flex justify-around text-center mb-4">
                <div>
                  <div className="text-4xl font-bold text-green-600">{wins}</div>
                  <div className="text-sm text-pocket-text-light">Wins</div>
                </div>
                <div className="text-3xl font-bold text-pocket-text-light">-</div>
                <div>
                  <div className="text-4xl font-bold text-red-600">{losses}</div>
                  <div className="text-sm text-pocket-text-light">Losses</div>
                </div>
              </div>

              {/* Battle indicators */}
              <div className="flex justify-center gap-3">
                {battles.map((_, index) => (
                  <div
                    key={index}
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${
                      battleResultsTracker[index] === 'win'
                        ? 'bg-green-500 text-white'
                        : battleResultsTracker[index] === 'loss'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>

            {/* Battles Remaining */}
            {battlesRemaining > 0 && (
              <div className="text-center mb-6">
                <p className="text-pocket-text-light">
                  {battlesRemaining} {battlesRemaining === 1 ? 'battle' : 'battles'} remaining
                </p>
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={handleContinueToNextBattle}
              className="w-full px-6 py-3 bg-pocket-red hover:bg-red-600 text-white font-bold rounded-xl transition-colors"
            >
              {battlesRemaining > 0 ? 'Continue to Next Battle' : 'View Final Results'}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show match result
  if (showMatchResult) {
    const winner = battleResults.winner || selectedReplay.winner;
    const player1Name = selectedReplay.player1_name || 'Player 1';
    const player2Name = selectedReplay.player2_name || 'Player 2';
    const winnerName = winner === 1 ? player1Name : player2Name;

    return (
      <div className="min-h-screen bg-pocket-bg p-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-card-lg p-8 text-center"
          >
            <div className="text-6xl mb-4 text-yellow-500">
              <Trophy className="mx-auto" />
            </div>
            <h2 className="text-3xl font-bold mb-2 text-yellow-600">
              Match Complete!
            </h2>
            <p className="text-pocket-text-light mb-6">
              {winnerName} wins!
            </p>

            {is3v3Format && battleResults.battlesWon && (
              <div className="bg-pocket-bg rounded-xl p-4 mb-6">
                <div className="flex justify-around text-center">
                  <div>
                    <div className="text-2xl font-bold text-pocket-text">
                      {battleResults.battlesWon.player1 || 0}
                    </div>
                    <div className="text-sm text-pocket-text-light">{player1Name}</div>
                  </div>
                  <div className="text-2xl font-bold text-pocket-text-light">-</div>
                  <div>
                    <div className="text-2xl font-bold text-pocket-text">
                      {battleResults.battlesWon.player2 || 0}
                    </div>
                    <div className="text-sm text-pocket-text-light">{player2Name}</div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setSelectedReplay(null);
                setGameState('tournaments');
              }}
              className="w-full px-6 py-3 bg-pocket-red hover:bg-red-600 text-white font-bold rounded-xl transition-colors"
            >
              Back to Tournaments
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default TournamentReplayScreen;
